import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//! Importing applicationDiagramAPI
import { applicationDiagramAPI } from "../applicationDiagrams/applicationDiagramsAPI";

//? Importing types
import { type Pagination } from "../../../services/stores/types";
import {
  type ApplicationDiagramDetails,
  type CreateApplicationDiagramInput,
} from "../applicationDiagrams/types/types";

/**
 * Custom hook `useApplicationDiagrams` para gerenciar operações relacionadas a ApplicationDiagrams
 * dentro do contexto de uma aplicação específica.
 *
 * @param {string} applicationId - ID da aplicação para operações contextuais.
 * @param {string} token - Token de autenticação para requisições à API.
 * @returns {Object} O hook retorna os dados dos applicationDiagrams, estado de carregamento, mensagens de erro,
 * e funções para interagir com a API.
 */
export const useApplicationDiagrams = (
  applicationId: string,
  token: string
) => {
  const queryClient = useQueryClient();

  // * Estados para pesquisa e paginação
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  // * Define a chave da query
  const queryKey = [
    "applicationDiagrams",
    applicationId,
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  // ? Busca os applicationDiagrams usando react-query
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      applicationDiagramAPI.getAll(
        applicationId,
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000, // * Cache por 5 minutos
    enabled: !!applicationId && !!token, // * Só executa a query se applicationId e token existirem
  });

  // * Extrai os dados e a paginação
  const applicationDiagrams = data?.applicationDiagrams || [];
  const fetchedPagination = data?.pagination || pagination;

  // ! Handlers para pesquisa e paginação
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // * Mutations para criar, atualizar e deletar applicationDiagrams
  const createApplicationDiagramMutation = useMutation({
    mutationFn: (applicationDiagramData: CreateApplicationDiagramInput) =>
      applicationDiagramAPI.create(
        applicationId,
        applicationDiagramData,
        token
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateApplicationDiagramMutation = useMutation({
    mutationFn: ({ id, diagram_id }: { id: string; diagram_id: string }) =>
      applicationDiagramAPI.update(applicationId, id, { diagram_id }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteApplicationDiagramMutation = useMutation({
    mutationFn: (id: string) =>
      applicationDiagramAPI.delete(applicationId, id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // TODO: Buscar detalhes de um applicationDiagram pelo ID
  const getApplicationDiagramById = useCallback(
    async (id: string): Promise<ApplicationDiagramDetails | null> => {
      try {
        const applicationDiagramDetails = await applicationDiagramAPI.getById(
          applicationId,
          id,
          token
        );
        return applicationDiagramDetails;
      } catch (err) {
        console.error("Error fetching application diagram by ID:", err);
        return null;
      }
    },
    [applicationId, token]
  );

  // * Retorna os valores e funções
  return {
    applicationDiagrams,
    loading: isLoading || isFetching,
    error: isError ? (error as Error).message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createApplicationDiagram: createApplicationDiagramMutation.mutate,
    updateApplicationDiagram: updateApplicationDiagramMutation.mutate,
    deleteApplicationDiagram: deleteApplicationDiagramMutation.mutate,
    getApplicationDiagramById,
    changePage,
    isCreating: createApplicationDiagramMutation.isPending,
    isUpdating: updateApplicationDiagramMutation.isPending,
    isDeleting: deleteApplicationDiagramMutation.isPending,
  };
};
